import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { type Archivo as ArchivoAPI } from '../../../services/archivosService'
import {
  XMarkIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  PhotoIcon,
  VideoCameraIcon,
  MusicalNoteIcon,
  ArchiveBoxIcon,
  TagIcon,
  EyeIcon,
  LockClosedIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  TrashIcon,
  PencilIcon
} from '@heroicons/react/24/outline'

type Archivo = ArchivoAPI & {
  extension?: string
  subcategoria?: string
  descripcion?: string
  tags?: string[]
  subidoPor?: string
  usuario?: string
  descargas?: number
  url?: string
  thumbnail?: string
  estado?: 'activo' | 'archivado' | 'eliminado'
  nivelAcceso?: 'publico' | 'privado' | 'restringido'
  version?: string
}

interface ModalArchivoProps {
  isOpen: boolean
  onClose: () => void
  tipo: 'ver' | 'editar' | 'eliminar' | 'subir'
  archivo?: Archivo | null
  onGuardar?: (archivo: Partial<Archivo>) => void
  onEliminar?: (id: string) => void
  onSubir?: (archivo: File, metadatos: Partial<Archivo>) => void
}

const ModalArchivo: React.FC<ModalArchivoProps> = ({
  isOpen,
  onClose,
  tipo,
  archivo,
  onGuardar,
  onEliminar,
  onSubir
}) => {
  const [formData, setFormData] = useState<Partial<Archivo>>({
    nombre: '',
    categoria: '',
    subcategoria: '',
    descripcion: '',
    tags: [],
    estado: 'activo',
    nivelAcceso: 'publico'
  })
  const [errores, setErrores] = useState<Record<string, string>>({})
  const [cargando, setCargando] = useState(false)
  const [archivoSeleccionado, setArchivoSeleccionado] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string>('')
  const [nuevoTag, setNuevoTag] = useState('')
  const [progreso, setProgreso] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const categorias = [
    'Manuales',
    'Presentaciones',
    'Reportes',
    'Recursos',
    'Capacitación',
    'Documentos Legales',
    'Imágenes',
    'Videos',
    'Audio',
    'Plantillas'
  ]

  const subcategorias: Record<string, string[]> = {
    'Manuales': ['Logística', 'Operaciones', 'Seguridad', 'Calidad'],
    'Presentaciones': ['Comercial', 'Técnica', 'Corporativa', 'Capacitación'],
    'Reportes': ['Mensuales', 'Anuales', 'Especiales', 'Análisis'],
    'Recursos': ['Branding', 'Formularios', 'Iconos', 'Imágenes'],
    'Capacitación': ['Videos', 'Documentos', 'Evaluaciones', 'Certificaciones']
  }

  useEffect(() => {
    if (archivo && (tipo === 'ver' || tipo === 'editar')) {
      setFormData({
        nombre: archivo.nombre,
        categoria: archivo.categoria,
        subcategoria: archivo.subcategoria,
        descripcion: archivo.descripcion,
        tags: archivo.tags,
        estado: archivo.estado,
        nivelAcceso: archivo.nivelAcceso
      })
    } else {
      setFormData({
        nombre: '',
        categoria: '',
        subcategoria: '',
        descripcion: '',
        tags: [],
        estado: 'activo',
        nivelAcceso: 'publico'
      })
    }
    setErrores({})
    setArchivoSeleccionado(null)
    setPreviewUrl('')
    setProgreso(0)
  }, [archivo, tipo, isOpen])

  const validarFormulario = (): boolean => {
    const nuevosErrores: Record<string, string> = {}

    if (tipo === 'subir' && !archivoSeleccionado) {
      nuevosErrores.archivo = 'Debe seleccionar un archivo'
    }

    if (!formData.nombre?.trim()) {
      nuevosErrores.nombre = 'El nombre es requerido'
    }

    if (!formData.categoria) {
      nuevosErrores.categoria = 'La categoría es requerida'
    }

    setErrores(nuevosErrores)
    return Object.keys(nuevosErrores).length === 0
  }

  const manejarCambioArchivo = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setArchivoSeleccionado(file)
      
      // Generar nombre automático si está vacío
      if (!formData.nombre) {
        setFormData(prev => ({
          ...prev,
          nombre: file.name
        }))
      }

      // Generar preview para imágenes
      if (file.type.startsWith('image/')) {
        const reader = new FileReader()
        reader.onload = (e) => {
          setPreviewUrl(e.target?.result as string)
        }
        reader.readAsDataURL(file)
      }

      // Determinar tipo automáticamente
      let tipoArchivo: 'documento' | 'imagen' | 'video' | 'audio' | 'archivo' = 'archivo'
      if (file.type.startsWith('image/')) tipoArchivo = 'imagen'
      else if (file.type.startsWith('video/')) tipoArchivo = 'video'
      else if (file.type.startsWith('audio/')) tipoArchivo = 'audio'
      else if (file.type.includes('pdf') || file.type.includes('document') || file.type.includes('text')) {
        tipoArchivo = 'documento'
      }

      setFormData(prev => ({
        ...prev,
        tipo: tipoArchivo,
        extension: file.name.split('.').pop() || '',
        tamaño: file.size
      }))
    }
  }

  const agregarTag = () => {
    if (nuevoTag.trim() && !formData.tags?.includes(nuevoTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), nuevoTag.trim()]
      }))
      setNuevoTag('')
    }
  }

  const eliminarTag = (tagAEliminar: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(tag => tag !== tagAEliminar) || []
    }))
  }

  const manejarSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validarFormulario()) return

    setCargando(true)
    setProgreso(0)

    try {
      // Simular progreso
      const interval = setInterval(() => {
        setProgreso(prev => {
          if (prev >= 90) {
            clearInterval(interval)
            return prev
          }
          return prev + 10
        })
      }, 100)

      await new Promise(resolve => setTimeout(resolve, 1000))
      
      setProgreso(100)
      clearInterval(interval)

      if (tipo === 'subir' && archivoSeleccionado && onSubir) {
        onSubir(archivoSeleccionado, formData)
      } else if (tipo === 'editar' && onGuardar) {
        onGuardar(formData)
      }

      onClose()
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setCargando(false)
      setProgreso(0)
    }
  }

  const manejarEliminar = async () => {
    if (archivo && onEliminar) {
      setCargando(true)
      try {
        await new Promise(resolve => setTimeout(resolve, 500))
        onEliminar(archivo.id)
        onClose()
      } catch (error) {
        console.error('Error:', error)
      } finally {
        setCargando(false)
      }
    }
  }

  const formatearTamaño = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const obtenerIconoTipo = (tipoArchivo?: string) => {
    switch (tipoArchivo) {
      case 'imagen':
        return <PhotoIcon className="h-8 w-8 text-green-500" />
      case 'video':
        return <VideoCameraIcon className="h-8 w-8 text-red-500" />
      case 'audio':
        return <MusicalNoteIcon className="h-8 w-8 text-purple-500" />
      case 'documento':
        return <DocumentIcon className="h-8 w-8 text-blue-500" />
      default:
        return <ArchiveBoxIcon className="h-8 w-8 text-gray-500" />
    }
  }

  const obtenerColorAcceso = (nivelAcceso: string) => {
    switch (nivelAcceso) {
      case 'publico':
        return 'text-green-600 bg-green-100'
      case 'privado':
        return 'text-red-600 bg-red-100'
      case 'restringido':
        return 'text-yellow-600 bg-yellow-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const obtenerIconoAcceso = (nivelAcceso: string) => {
    switch (nivelAcceso) {
      case 'publico':
        return <GlobeAltIcon className="h-4 w-4" />
      case 'privado':
        return <LockClosedIcon className="h-4 w-4" />
      case 'restringido':
        return <ShieldCheckIcon className="h-4 w-4" />
      default:
        return <GlobeAltIcon className="h-4 w-4" />
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              {tipo === 'ver' && <EyeIcon className="h-6 w-6 text-blue-600" />}
              {tipo === 'editar' && <PencilIcon className="h-6 w-6 text-green-600" />}
              {tipo === 'eliminar' && <TrashIcon className="h-6 w-6 text-red-600" />}
              {tipo === 'subir' && <CloudArrowUpIcon className="h-6 w-6 text-purple-600" />}
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {tipo === 'ver' && 'Ver Archivo'}
                {tipo === 'editar' && 'Editar Archivo'}
                {tipo === 'eliminar' && 'Eliminar Archivo'}
                {tipo === 'subir' && 'Subir Archivo'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {tipo === 'eliminar' ? (
              // Vista de eliminación
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900 mb-4">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  ¿Estás seguro de eliminar este archivo?
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Esta acción no se puede deshacer. El archivo "{archivo?.nombre}" será eliminado permanentemente.
                </p>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
                  <div className="flex items-center space-x-3">
                    {obtenerIconoTipo(archivo?.tipo)}
                    <div className="text-left">
                      <p className="font-medium text-gray-900 dark:text-white">{archivo?.nombre}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {archivo?.categoria} • {formatearTamaño(archivo?.tamaño || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Vista de ver/editar/subir
              <form onSubmit={manejarSubmit} className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Columna izquierda */}
                  <div className="space-y-6">
                    {/* Selección de archivo (solo para subir) */}
                    {tipo === 'subir' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Archivo *
                        </label>
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors duration-200 ${
                            archivoSeleccionado
                              ? 'border-green-300 bg-green-50 dark:bg-green-900 dark:border-green-600'
                              : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
                          }`}
                        >
                          <input
                            ref={fileInputRef}
                            type="file"
                            onChange={manejarCambioArchivo}
                            className="hidden"
                            accept="*/*"
                          />
                          {archivoSeleccionado ? (
                            <div className="space-y-2">
                              <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto" />
                              <p className="text-sm font-medium text-green-700 dark:text-green-300">
                                {archivoSeleccionado.name}
                              </p>
                              <p className="text-xs text-green-600 dark:text-green-400">
                                {formatearTamaño(archivoSeleccionado.size)}
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto" />
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                Haz clic para seleccionar un archivo
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-500">
                                Todos los formatos son compatibles
                              </p>
                            </div>
                          )}
                        </div>
                        {errores.archivo && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errores.archivo}</p>
                        )}
                      </div>
                    )}

                    {/* Preview (solo para ver y si hay thumbnail) */}
                    {tipo === 'ver' && archivo?.thumbnail && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Vista previa
                        </label>
                        <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                          <img
                            src={archivo.thumbnail}
                            alt={archivo.nombre}
                            className="w-full h-48 object-cover rounded"
                          />
                        </div>
                      </div>
                    )}

                    {/* Preview para subida */}
                    {tipo === 'subir' && previewUrl && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Vista previa
                        </label>
                        <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                          <img
                            src={previewUrl}
                            alt="Preview"
                            className="w-full h-48 object-cover rounded"
                          />
                        </div>
                      </div>
                    )}

                    {/* Información del archivo (solo para ver) */}
                    {tipo === 'ver' && archivo && (
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                        <h3 className="font-medium text-gray-900 dark:text-white">Información del archivo</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Tamaño:</span>
                            <span className="ml-2 text-gray-900 dark:text-white">{formatearTamaño(archivo.tamaño)}</span>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Tipo:</span>
                            <span className="ml-2 text-gray-900 dark:text-white">{(archivo.extension || '').toUpperCase()}</span>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Subido por:</span>
                            <span className="ml-2 text-gray-900 dark:text-white">{archivo.subidoPor}</span>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Fecha:</span>
                            <span className="ml-2 text-gray-900 dark:text-white">
                              {new Date(archivo.fechaSubida).toLocaleDateString('es-ES')}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Descargas:</span>
                            <span className="ml-2 text-gray-900 dark:text-white">{archivo.descargas}</span>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Versión:</span>
                            <span className="ml-2 text-gray-900 dark:text-white">{archivo.version}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Columna derecha */}
                  <div className="space-y-6">
                    {/* Nombre */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Nombre del archivo *
                      </label>
                      <input
                        type="text"
                        value={formData.nombre || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                        disabled={tipo === 'ver'}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                        placeholder="Nombre del archivo"
                      />
                      {errores.nombre && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errores.nombre}</p>
                      )}
                    </div>

                    {/* Categoría */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Categoría *
                      </label>
                      <select
                        value={formData.categoria || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, categoria: e.target.value, subcategoria: '' }))}
                        disabled={tipo === 'ver'}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                      >
                        <option value="">Seleccionar categoría</option>
                        {categorias.map(categoria => (
                          <option key={categoria} value={categoria}>{categoria}</option>
                        ))}
                      </select>
                      {errores.categoria && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errores.categoria}</p>
                      )}
                    </div>

                    {/* Subcategoría */}
                    {formData.categoria && subcategorias[formData.categoria] && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Subcategoría
                        </label>
                        <select
                          value={formData.subcategoria || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, subcategoria: e.target.value }))}
                          disabled={tipo === 'ver'}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                        >
                          <option value="">Seleccionar subcategoría</option>
                          {subcategorias[formData.categoria].map(subcategoria => (
                            <option key={subcategoria} value={subcategoria}>{subcategoria}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Descripción */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Descripción
                      </label>
                      <textarea
                        value={formData.descripcion || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                        disabled={tipo === 'ver'}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                        placeholder="Descripción del archivo"
                      />
                    </div>

                    {/* Tags */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Etiquetas
                      </label>
                      <div className="space-y-2">
                        {tipo !== 'ver' && (
                          <div className="flex space-x-2">
                            <input
                              type="text"
                              value={nuevoTag}
                              onChange={(e) => setNuevoTag(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), agregarTag())}
                              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                              placeholder="Agregar etiqueta"
                            />
                            <button
                              type="button"
                              onClick={agregarTag}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
                            >
                              Agregar
                            </button>
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2">
                          {formData.tags?.map((tag: string, index: number) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
                            >
                              <TagIcon className="h-3 w-3 mr-1" />
                              {tag}
                              {tipo !== 'ver' && (
                                <button
                                  type="button"
                                  onClick={() => eliminarTag(tag)}
                                  className="ml-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                                >
                                  <XMarkIcon className="h-3 w-3" />
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Estado y Nivel de acceso */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Estado
                        </label>
                        <select
                          value={formData.estado || 'activo'}
                          onChange={(e) => setFormData(prev => ({ ...prev, estado: e.target.value as any }))}
                          disabled={tipo === 'ver'}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                        >
                          <option value="activo">Activo</option>
                          <option value="archivado">Archivado</option>
                          <option value="eliminado">Eliminado</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Nivel de acceso
                        </label>
                        <select
                          value={formData.nivelAcceso || 'publico'}
                          onChange={(e) => setFormData(prev => ({ ...prev, nivelAcceso: e.target.value as any }))}
                          disabled={tipo === 'ver'}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                        >
                          <option value="publico">Público</option>
                          <option value="privado">Privado</option>
                          <option value="restringido">Restringido</option>
                        </select>
                      </div>
                    </div>

                    {/* Información de acceso */}
                    {formData.nivelAcceso && (
                      <div className={`p-3 rounded-lg ${obtenerColorAcceso(formData.nivelAcceso)}`}>
                        <div className="flex items-center space-x-2">
                          {obtenerIconoAcceso(formData.nivelAcceso)}
                          <span className="text-sm font-medium">
                            {formData.nivelAcceso === 'publico' && 'Acceso público - Visible para todos'}
                            {formData.nivelAcceso === 'privado' && 'Acceso privado - Solo administradores'}
                            {formData.nivelAcceso === 'restringido' && 'Acceso restringido - Usuarios autorizados'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Barra de progreso (solo para subir) */}
                {tipo === 'subir' && progreso > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Subiendo archivo...</span>
                      <span className="text-gray-600 dark:text-gray-400">{progreso}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progreso}%` }}
                      />
                    </div>
                  </div>
                )}
              </form>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              disabled={cargando}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50"
            >
              Cancelar
            </button>
            
            {tipo === 'eliminar' ? (
              <button
                onClick={manejarEliminar}
                disabled={cargando}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cargando ? 'Eliminando...' : 'Eliminar'}
              </button>
            ) : tipo !== 'ver' ? (
              <button
                onClick={manejarSubmit}
                disabled={cargando}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cargando ? (
                  tipo === 'subir' ? 'Subiendo...' : 'Guardando...'
                ) : (
                  tipo === 'subir' ? 'Subir Archivo' : 'Guardar Cambios'
                )}
              </button>
            ) : (
              <button
                onClick={onClose}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                Cerrar
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export default ModalArchivo
